import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import {
    Table, TableBody, TableCell, tableCellClasses, TableContainer, TableHead, TableRow,
    Paper, IconButton, Chip, Pagination, Box, Typography, Tooltip,
    Dialog, DialogTitle, DialogContent, DialogActions, Button
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import EmailIcon from '@mui/icons-material/Email';
import { toast } from 'react-toastify';
import { deleteExternalReferrerRequest } from '../../../redux/modules/externalReferrer/slice';
import { sendResetEmailRequest, clearSendResetEmail } from '../../../redux/modules/staff/slice';
import ManageOrdersDialog from './ManageOrdersDialog';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));

const ExternalReferrerList = ({ data, totalCount, page, onPageChange, onEdit, loading }) => {
    const dispatch = useDispatch();
    const { sendResetEmailRes } = useSelector((state) => state.staff);
    const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null, name: '' });
    const [manageOrdersOpen, setManageOrdersOpen] = useState(false);
    const [selectedReferrer, setSelectedReferrer] = useState(null);
    const [emailModal, setEmailModal] = useState({ open: false, userId: null, userName: '' });

    // Handle send reset email response
    useEffect(() => {
        if (!sendResetEmailRes) return;
        if (sendResetEmailRes?.success) {
            toast.success('Password reset email sent successfully');
        } else if (sendResetEmailRes?.message) {
            toast.error(sendResetEmailRes?.message || 'Failed to send reset email');
        }
        dispatch(clearSendResetEmail());
    }, [sendResetEmailRes, dispatch]);

    const handleDelete = () => {
        if (deleteConfirm.id) {
            dispatch(deleteExternalReferrerRequest(deleteConfirm.id));
            setDeleteConfirm({ open: false, id: null, name: '' });
        }
    };

    const handleManageOrders = (referrer) => {
        setSelectedReferrer(referrer);
        setManageOrdersOpen(true);
    };

    const handleSendResetEmail = (referrer) => {
        setEmailModal({
            open: true,
            userId: referrer._id,
            userName: `${referrer.firstName} ${referrer.lastName}`
        });
    };

    const confirmSendResetEmail = () => {
        dispatch(sendResetEmailRequest(emailModal.userId));
        setEmailModal({ open: false, userId: null, userName: '' });
    };

    const formatDate = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString();
    };

    return (
        <>
            <Box sx={{ overflowX: { xs: 'auto', md: 'visible' }, mx: { xs: -2, sm: 0 }, px: { xs: 2, sm: 0 } }}>
            <TableContainer component={Paper} className='tableShadow'>
                <Table sx={{ minWidth: { xs: 700, md: 900 } }} className='commonTable'>
                    <TableHead>
                        <TableRow>
                            <StyledTableCell>Name</StyledTableCell>
                            <StyledTableCell>Email</StyledTableCell>
                            <StyledTableCell>Organization</StyledTableCell>
                            <StyledTableCell align="center">Active Orders</StyledTableCell>
                            <StyledTableCell>Last Access</StyledTableCell>
                            <StyledTableCell>Created</StyledTableCell>
                            <StyledTableCell align="center">Actions</StyledTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <StyledTableRow>
                                <StyledTableCell colSpan={7} align="center">Loading...</StyledTableCell>
                            </StyledTableRow>
                        ) : data.length === 0 ? (
                            <StyledTableRow>
                                <StyledTableCell colSpan={7} align="center">
                                    <Typography color="text.secondary">No external users found</Typography>
                                </StyledTableCell>
                            </StyledTableRow>
                        ) : (
                            data.map((referrer) => (
                                <StyledTableRow key={referrer._id}>
                                    <StyledTableCell>
                                        <Typography fontWeight="medium">
                                            {referrer.firstName} {referrer.lastName}
                                        </Typography>
                                    </StyledTableCell>
                                    <StyledTableCell>{referrer.email}</StyledTableCell>
                                    <StyledTableCell>{referrer.organization || '-'}</StyledTableCell>
                                    <StyledTableCell align="center">
                                        {referrer.accessAllOrders ? (
                                            <Chip
                                                label="All Access"
                                                size="small"
                                                sx={{
                                                    fontWeight: 600,
                                                    bgcolor: 'success.main',
                                                    color: 'success.contrastText',
                                                }}
                                            />
                                        ) : (
                                            <Chip
                                                label={referrer.activeOrderCount || 0}
                                                size="small"
                                                sx={{
                                                    fontWeight: 600,
                                                    minWidth: 32,
                                                    ...(referrer.activeOrderCount > 0 ? {
                                                        bgcolor: 'primary.main',
                                                        color: 'primary.contrastText',
                                                    } : {
                                                        bgcolor: 'grey.200',
                                                        color: 'text.primary',
                                                    })
                                                }}
                                            />
                                        )}
                                    </StyledTableCell>
                                    <StyledTableCell>{formatDate(referrer.lastAccessedAt)}</StyledTableCell>
                                    <StyledTableCell>{formatDate(referrer.createdAt)}</StyledTableCell>
                                    <StyledTableCell align="center">
                                        <Tooltip title="Manage Orders">
                                            <IconButton
                                                color="primary"
                                                onClick={() => handleManageOrders(referrer)}
                                            >
                                                <FolderSharedIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Send Password Reset Email">
                                            <IconButton
                                                color="info"
                                                onClick={() => handleSendResetEmail(referrer)}
                                            >
                                                <EmailIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Edit">
                                            <IconButton
                                                color="primary"
                                                onClick={() => onEdit(referrer)}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete">
                                            <IconButton
                                                color="error"
                                                onClick={() => setDeleteConfirm({
                                                    open: true,
                                                    id: referrer._id,
                                                    name: `${referrer.firstName} ${referrer.lastName}`
                                                })}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </StyledTableCell>
                                </StyledTableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            </Box>

            {totalCount > 10 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination
                        count={Math.ceil(totalCount / 10)}
                        page={page}
                        onChange={(e, value) => onPageChange(value)}
                        color="primary"
                    />
                </Box>
            )}

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteConfirm.open} onClose={() => setDeleteConfirm({ open: false, id: null, name: '' })}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete <strong>{deleteConfirm.name}</strong>?
                        This will also revoke all their order access.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirm({ open: false, id: null, name: '' })}>
                        Cancel
                    </Button>
                    <Button onClick={handleDelete} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Email Confirmation Dialog */}
            <Dialog open={emailModal.open} onClose={() => setEmailModal({ open: false, userId: null, userName: '' })}>
                <DialogTitle>Send Password Reset Email</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to send a password reset email to <strong>{emailModal.userName}</strong>?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEmailModal({ open: false, userId: null, userName: '' })}>
                        Cancel
                    </Button>
                    <Button onClick={confirmSendResetEmail} variant="contained" color="primary">
                        Send Email
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Manage Orders Dialog */}
            <ManageOrdersDialog
                open={manageOrdersOpen}
                onClose={() => {
                    setManageOrdersOpen(false);
                    setSelectedReferrer(null);
                }}
                externalUser={selectedReferrer}
            />
        </>
    );
};

export default ExternalReferrerList;
